import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { dashboardKeys, tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";
import { getDashboard } from "@/services/dashboardService";

/**
 * Stable alert ids used by the dashboard's onboarding nudges. Mirrors
 * what DashboardOnboardingCard writes via dismissDashboardAlert. Kept
 * in sync to avoid drift; we read from the dashboard response's
 * dismissedAlertIds[] in both places.
 */
// CFE import is two independent steps — emitted (what the company issued) and
// received (what suppliers sent). Each has its own dismiss flag so the owner
// can mark / import them separately. The emitted flag keeps the legacy
// "onboarding-cfe-import" key so existing stamps still count as done.
export const ONBOARDING_NUDGE_CFE_IMPORT = "onboarding-cfe-import";
export const ONBOARDING_NUDGE_CFE_IMPORT_RECIBIDAS =
  "onboarding-cfe-import-recibidas";
// Owner-side "Cargá tus CAE" dashboard step. Completion is an explicit dismiss
// (a tenant loads as many CAE types as they use, then marks it done) — there is
// no fixed CAE count we could auto-complete on.
export const ONBOARDING_NUDGE_CAE = "onboarding-cae";
export const ONBOARDING_NUDGE_BRAND = "onboarding-brand";
export const ONBOARDING_NUDGE_ACCOUNTANT_INVITE =
  "onboarding-accountant-invite";
/** Accountant-flow marks: "CAEs uploaded" and "historical CFEs imported".
 *  No native backend signal exists at the tenant level so the accountant
 *  stamps these manually from the client-onboarding checklist. Persisted
 *  in company_dismissed_dashboard_alert (tenant-scoped) so different
 *  devices / coworkers see the same progress. */
export const ONBOARDING_NUDGE_ACCT_CAE_LOADED = "onboarding-acct-cae-loaded";
export const ONBOARDING_NUDGE_ACCT_CFE_HISTORY_IMPORTED =
  "onboarding-acct-cfe-history-imported";
/** Accountant confirmed the tenant's active CdgDGISucur value. Stamped
 *  from the accountant client-onboarding checklist's "Confirmar sucursal"
 *  step so the same coworker on a different device sees it acknowledged. */
export const ONBOARDING_NUDGE_ACCT_SUCURSAL_CONFIRMED =
  "onboarding-acct-sucursal-confirmed";
/** Phase 2 of the accountant onboarding (Fiscalo branding: public name,
 *  logo, brand color). Stamped when the accountant finalises the panel.
 *  Kept the legacy key string so existing stamps in
 *  company_dismissed_dashboard_alert still mark this phase as done — the
 *  scope used to include fiscal profile, but we split that into phase 3
 *  below and a re-stamp isn't worth the migration. */
export const ONBOARDING_NUDGE_ACCT_FISCALO_CONFIG_CONFIRMED =
  "onboarding-acct-fiscalo-config-confirmed";
/** Phase 3 of the accountant onboarding (fiscal profile: company type,
 *  IVA regime + minimum benefit, IRAE frequency, etc). Stamped when the
 *  accountant confirms the perfil fiscal form. Split out from phase 2
 *  because the fiscal profile is dense enough to merit its own step. */
export const ONBOARDING_NUDGE_ACCT_FISCAL_PROFILE_CONFIRMED =
  "onboarding-acct-fiscal-profile-confirmed";
/** Phase 4 of the accountant onboarding (ownership transfer). Stamped
 *  whether the accountant invited the real owner or explicitly chose to
 *  stay as owner for now. Either resolution ends the wizard. */
export const ONBOARDING_NUDGE_ACCT_TRANSFER_RESOLVED =
  "onboarding-acct-transfer-resolved";

type AutoStepKey =
  | "portal_user"
  | "company_info"
  | "cae_download"
  | "proveedor_software";

const AUTO_STEPS: ReadonlyArray<AutoStepKey> = [
  "portal_user",
  "company_info",
  "cae_download",
  "proveedor_software",
];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

export interface OnboardingPendingState {
  /** True when any onboarding item is still outstanding. */
  hasPending: boolean;
  certPending: boolean;
  cfeImportPending: boolean;
  /** True when the user kicked off the DGI auto-flow and it hasn't finished. */
  autoFlowIncomplete: boolean;
  /** True when the auto-flow has a `_failed` marker on any step. */
  autoFlowFailed: boolean;
  /** Total number of pending items (cert + CFE + 1 if auto-flow incomplete). */
  pendingCount: number;
  /** Loading the tenant fetch — callers can choose to gate UI on this. */
  isLoading: boolean;
}

/**
 * Centralized check for "is the user mid-onboarding". Used by the dashboard
 * page (to hide normal panels) and by the onboarding card (to render itself).
 * Sharing the logic keeps both surfaces in sync — the rule "card visible iff
 * dashboard rest hidden" holds without manual coordination.
 */
export function useOnboardingPending(): OnboardingPendingState {
  const { user } = useAuth();
  const { data: tenant, isLoading } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  // Same query key as useDashboardMetrics so React Query dedupes — no
  // extra fetch when both this hook and dashboard-page run.
  const { data: dashboard } = useQuery({
    queryKey: dashboardKeys.metrics(),
    queryFn: getDashboard,
    enabled: Boolean(tenant),
  });
  if (!tenant) {
    return {
      hasPending: false,
      certPending: false,
      cfeImportPending: false,
      autoFlowIncomplete: false,
      autoFlowFailed: false,
      pendingCount: 0,
      isLoading,
    };
  }

  // bypassDgi tenants (demo / showcase) never need a cert — keep this
  // in sync with DashboardOnboardingCard's certPending gate, otherwise
  // the hook says "still onboarding" → dashboard hides its panels,
  // while the card itself decides it has nothing to render → returns
  // null. Result: blank dashboard.
  const certPending = !tenant.bypassDgi && !tenant.hasDgiCredentials;
  // CFE-import dismissal lives on the backend now (company_dismissed_
  // dashboard_alert table, alertId='onboarding-cfe-import'). Same row
  // drives DashboardOnboardingCard's render gate so the two surfaces
  // can't disagree. Read returns false (= not pending) while the
  // dashboard query is loading so we don't briefly gate the panels.
  const dismissedAlertIds = dashboard?.dismissedAlertIds ?? [];
  // CFE import is now two sub-steps (emitted + received) — "pending" means
  // either is still outstanding so the dashboard gate behaves as before.
  const cfeEmitidosPending = dashboard
    ? !dismissedAlertIds.includes(ONBOARDING_NUDGE_CFE_IMPORT)
    : false;
  const cfeRecibidasPending = dashboard
    ? !dismissedAlertIds.includes(ONBOARDING_NUDGE_CFE_IMPORT_RECIBIDAS)
    : false;
  const cfeImportPending = cfeEmitidosPending || cfeRecibidasPending;

  const progress = isRecord(tenant.dgiAutoOnboardingProgress)
    ? tenant.dgiAutoOnboardingProgress
    : null;
  const autoFlowStarted = Boolean(progress?.portal_user);
  // Scratch tenants skip proveedor_software during onboarding — Fiscalo
  // isn't yet a DGI-homologated proveedor for them, so that key never
  // stamps and gating "done" on it would leave the dashboard reporting
  // the auto-flow as incomplete forever.
  const isScratchTenant = tenant.onboardingSituationChoice === "scratch";
  const autoFlowDone =
    autoFlowStarted &&
    AUTO_STEPS.every((k) => {
      if (k === "proveedor_software" && isScratchTenant) return true;
      return Boolean(progress?.[k]);
    });
  const autoFlowIncomplete = autoFlowStarted && !autoFlowDone;
  const failed = isRecord(progress?._failed) ? progress!._failed : null;
  const autoFlowFailed = AUTO_STEPS.some(
    (k) => failed && Boolean((failed as Record<string, unknown>)[k]),
  );

  const pendingCount =
    (certPending ? 1 : 0) +
    (cfeEmitidosPending ? 1 : 0) +
    (cfeRecibidasPending ? 1 : 0) +
    (autoFlowIncomplete ? 1 : 0);

  return {
    hasPending: pendingCount > 0,
    certPending,
    cfeImportPending,
    autoFlowIncomplete,
    autoFlowFailed,
    pendingCount,
    isLoading,
  };
}
